/**
 * W5 — GlobalSearch command palette helpers
 * WB4-CP1 — palette operating layer execution (manifest handler coverage + surface: palette).
 */

import type { NavigateFunction } from 'react-router-dom';
import type { ResolvedCommand } from '@shared/business-workbench/command-resolver';
import {
  partitionCommandsBySection,
  resolveCommands,
  resolvePaletteGroupedCommands,
  type WorkbenchCommandContext,
} from '@shared/business-workbench/command-resolver';
import { isManifestKernelHandlerRefDispatchable } from '@shared/business-workbench/domain-kernel-handler-coverage';
import {
  COMMAND_PALETTE_GROUP_ORDER,
  flattenPaletteGroupedCommands,
  isExplainPaletteHandlerRef,
  type PaletteGroupedCommands,
} from '@shared/business-workbench/workbench-command-palette-contract';
import { resolveOperationalPaletteCommandsFromQuery } from '@shared/business-workbench/workbench-palette-operational-query';
import {
  isPaletteOperationalFilterHandlerRef,
  resolvePaletteOperationalFilterNavigation,
} from '@shared/business-workbench/workbench-palette-operational-filter';
import {
  buildWorkbenchExecutionResult,
  resolveActionRiskTier,
} from '@shared/business-workbench/workbench-action-safety';
import type { WorkbenchKernelDispatchDeps } from '../components/workbench/ActionExecutor';
import { recordWorkbenchKernelAction } from '../components/workbench/ActionExecutor';
import { dispatchWorkbenchKernelHandlerWithGateResult } from '../components/workbench/workbench-gated-kernel-dispatch';
import { applyWorkbenchKernelDispatchFeedback } from '../components/workbench/workbench-kernel-dispatch-feedback';
import {
  emitWorkbenchPaletteExplainRequest,
  parseExplainPaletteSourceActionId,
} from '../workbench/workbench-palette-explain-bridge';
import { getActiveWorkbenchCommandFocus } from '../workbench/workbench-command-focus-bridge';

export interface PartitionedCommands {
  route: ResolvedCommand[];
  action: ResolvedCommand[];
}

export interface WorkbenchPaletteCommands extends PartitionedCommands {
  grouped: PaletteGroupedCommands;
}

export interface WorkbenchCommandExecuteOptions {
  kernelDeps?: WorkbenchKernelDispatchDeps;
}

function isFlowStartRef(ref: string | undefined): boolean {
  return ref?.startsWith('flow:start:') ?? false;
}

export function isExecutableWorkbenchCommand(command: ResolvedCommand): boolean {
  if (command.disabled) {
    return false;
  }
  if (command.route) {
    return true;
  }
  const handlerRef = command.handlerRef ?? command.actionRef;
  if (isExplainPaletteHandlerRef(handlerRef)) {
    return true;
  }
  return handlerRef ? isManifestKernelHandlerRefDispatchable(handlerRef) : false;
}

function filterGroupedForDisplay(grouped: PaletteGroupedCommands): PaletteGroupedCommands {
  const filtered = {} as PaletteGroupedCommands;
  for (const group of COMMAND_PALETTE_GROUP_ORDER) {
    filtered[group] = grouped[group].filter((command) => {
      if (group === 'explain') {
        return isExplainPaletteHandlerRef(command.handlerRef ?? command.actionRef);
      }
      return isExecutableWorkbenchCommand(command);
    });
  }
  return filtered;
}

function mergeOperationalCommandsIntoGrouped(
  grouped: PaletteGroupedCommands,
  ctx: WorkbenchCommandContext,
  query: string
): PaletteGroupedCommands {
  const operational = resolveOperationalPaletteCommandsFromQuery(ctx, query).filter(
    isExecutableWorkbenchCommand
  );
  if (operational.length === 0) {
    return grouped;
  }

  const merged = { ...grouped };
  for (const group of COMMAND_PALETTE_GROUP_ORDER) {
    merged[group] = [...grouped[group]];
  }

  for (const command of operational) {
    const group = command.group ?? 'filter';
    const bucket = merged[group];
    const handlerRef = command.handlerRef ?? command.actionRef;
    const duplicate = bucket.some(
      (existing) =>
        existing.id === command.id ||
        (handlerRef !== undefined && (existing.handlerRef ?? existing.actionRef) === handlerRef)
    );
    if (!duplicate) {
      merged[group] = [command, ...bucket];
    }
  }

  return merged;
}

export function resolvePaletteOperatingLayerCommands(
  ctx: WorkbenchCommandContext,
  query = ''
): WorkbenchPaletteCommands {
  const baseGrouped = filterGroupedForDisplay(resolvePaletteGroupedCommands(ctx, query));
  const grouped = mergeOperationalCommandsIntoGrouped(baseGrouped, ctx, query);
  const flat = flattenPaletteGroupedCommands(grouped);
  const { route, action } = partitionCommandsBySection([...flat]);
  return { grouped, route, action };
}

/** @deprecated Prefer resolvePaletteOperatingLayerCommands — kept for route/action consumers. */
export function resolvePartitionedCommands(
  ctx: WorkbenchCommandContext,
  query = ''
): PartitionedCommands {
  const { route, action } = resolvePaletteOperatingLayerCommands(ctx, query);
  return { route, action };
}

function resolvePaletteKernelDeps(
  kernelDeps: WorkbenchKernelDispatchDeps | undefined
): WorkbenchKernelDispatchDeps | undefined {
  if (!kernelDeps) {
    return undefined;
  }
  return {
    ...kernelDeps,
    surface: 'palette',
  };
}

export function executeWorkbenchCommand(
  command: ResolvedCommand,
  navigate: NavigateFunction,
  options: WorkbenchCommandExecuteOptions = {}
): 'navigated' | 'executed' | 'noop' {
  if (command.disabled) {
    return 'noop';
  }

  if (command.route) {
    navigate(command.route);
    return 'navigated';
  }

  const handlerRef = command.handlerRef ?? command.actionRef;
  if (!handlerRef) {
    return 'noop';
  }

  const kernelDeps = resolvePaletteKernelDeps(options.kernelDeps);
  const action = {
    id: command.id,
    label: command.label,
    handlerRef,
  };

  if (isExplainPaletteHandlerRef(handlerRef)) {
    const sourceActionId = parseExplainPaletteSourceActionId(handlerRef) ?? command.id;
    const focus = getActiveWorkbenchCommandFocus() ?? undefined;
    const objectRef = kernelDeps?.objectRef;
    emitWorkbenchPaletteExplainRequest({
      tenantId: kernelDeps?.tenantId ?? 'default_tenant',
      actionId: sourceActionId,
      actionLabel: command.label,
      ...(command.reason?.trim() ? { staticReason: command.reason.trim() } : {}),
      ...(focus !== undefined ? { focus } : {}),
      ...(objectRef
        ? {
            entity: {
              id: objectRef.id,
              ...(objectRef.label ? { name: objectRef.label } : {}),
              businessDomain: objectRef.businessDomain,
            },
          }
        : {}),
    });
    return 'executed';
  }

  if (isPaletteOperationalFilterHandlerRef(handlerRef)) {
    const navigation = resolvePaletteOperationalFilterNavigation(handlerRef, {
      ...(kernelDeps?.entitySnapshot &&
      typeof kernelDeps.entitySnapshot === 'object' &&
      'id' in kernelDeps.entitySnapshot &&
      kernelDeps.entitySnapshot.id
        ? { object: { id: String(kernelDeps.entitySnapshot.id) } }
        : {}),
      ...(kernelDeps?.objectRef.businessDomain
        ? { businessDomain: kernelDeps.objectRef.businessDomain }
        : {}),
    });
    if (!navigation) {
      return 'noop';
    }

    if (kernelDeps) {
      recordWorkbenchKernelAction({
        tenantId: kernelDeps.tenantId,
        objectRef: kernelDeps.objectRef,
        action,
        surface: 'palette',
      });
      applyWorkbenchKernelDispatchFeedback(
        buildWorkbenchExecutionResult({
          ok: true,
          handled: true,
          actionId: action.id,
          objectRef: kernelDeps.objectRef,
          riskPolicy: resolveActionRiskTier({
            actionId: action.id,
            handlerRef,
            businessDomain: kernelDeps.objectRef.businessDomain,
          }),
          userMessage: { title: command.label },
        }),
        action
      );
    }

    navigate(`${navigation.pathname}${navigation.search}`);
    return 'navigated';
  }

  if (kernelDeps) {
    const result = dispatchWorkbenchKernelHandlerWithGateResult(action, {
      ...kernelDeps,
      navigate,
    });
    applyWorkbenchKernelDispatchFeedback(result, action);
    if (result.handled) {
      return 'executed';
    }
  }

  if (isFlowStartRef(handlerRef)) {
    navigate('/workspace');
    return 'navigated';
  }

  return 'noop';
}

/** Test helper — exposes raw resolver without display filtering. */
export function resolveRawPaletteGroupedCommands(
  ctx: WorkbenchCommandContext,
  query = ''
): PaletteGroupedCommands {
  return resolvePaletteGroupedCommands(ctx, query);
}

/** Test helper — exposes resolveCommands for resolver unit tests. */
export { resolveCommands };
